import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../store/auth.store';
import { 
  Search, 
  ShoppingCart, 
  Plus, 
  Minus, 
  Trash2, 
  CreditCard, 
  Banknote,
  Smartphone,
  Printer,
  X,
  User,
  Package,
  CheckCircle,
  ScanBarcode,
  Loader2,
  Tag,
  Stethoscope,
  ShoppingBag,
  ArrowRight,
  MapPin
} from 'lucide-react';
import { OrdersService } from '../services/orders/orders.service';
import { TicketThermal } from '../components/pos/TicketThermal';
import type { ProductSearchResult } from '../services/products/products.service';
import { ProductsService } from '../services/products/products.service';
import { getFirstProductImage } from '../utils/product-images';
import { printThermalTicket } from '../utils/print-ticket';
import { ClienteSearchModal } from '../components/pos/ClienteSearchModal';
import toast from 'react-hot-toast';

interface ItemVenta {
  producto: ProductSearchResult;
  presentacionId: string;
  presentacionNombre: string;
  factor: number;
  cantidad: number;
  subtotal: number;
}

interface ClientePOS {
  id?: string;
  nombre: string;
  email?: string;
  telefono?: string;
  documento?: string;
}

type MetodoPago = 'EFECTIVO' | 'TARJETA' | 'NEQUI' | 'DAVIPLATA' | 'TRANSFERENCIA';

const METODOS_PAGO = [
  { id: 'EFECTIVO' as MetodoPago, label: 'Efectivo', icon: Banknote },
  { id: 'TRANSFERENCIA' as MetodoPago, label: 'Transf.', icon: Tag },
  { id: 'NEQUI' as MetodoPago, label: 'Nequi', icon: Smartphone },
  { id: 'TARJETA' as MetodoPago, label: 'Tarjeta', icon: CreditCard },
  { id: 'DAVIPLATA' as MetodoPago, label: 'Daviplata', icon: Smartphone },
];



export const POSPage: React.FC = () => {
  const { user } = useAuthStore();
  const [busqueda, setBusqueda] = useState('');
  const [productosFiltrados, setProductosFiltrados] = useState<ProductSearchResult[]>([]);
  const [items, setItems] = useState<ItemVenta[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedPresIndex, setSelectedPresIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [cliente, setCliente] = useState<ClientePOS | null>(null);
  const [metodoPago, setMetodoPago] = useState<MetodoPago>('EFECTIVO');
  const [metodoPagoIndex, setMetodoPagoIndex] = useState(0);
  const [efectivoRecibido, setEfectivoRecibido] = useState<number>(0);
  const [pagos, setPagos] = useState<Array<{ metodo: MetodoPago; monto: number }>>([]);
  const [mostrarModalPago, setMostrarModalPago] = useState(false);
  const [mostrarModalCliente, setMostrarModalCliente] = useState(false);
  const [ventaCompletada, setVentaCompletada] = useState(false);
  const [numeroPedido, setNumeroPedido] = useState('');
  const [pedidoId, setPedidoId] = useState('');
  const [mostrarModalAnulacion, setMostrarModalAnulacion] = useState(false);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [loadingAnulacion, setLoadingAnulacion] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mostrarSelectorPresentacion, setMostrarSelectorPresentacion] = useState(false);
  const [productoParaSeleccionar, setProductoParaSeleccionar] = useState<ProductSearchResult | null>(null);
  const [ultimaBusquedaEjecutada, setUltimaBusquedaEjecutada] = useState('');
  // IVA deshabilitado (siempre 0)
  const [ticketData, setTicketData] = useState<{
    items: ItemVenta[];
    subtotal: number;
    iva: number;
    total: number;
    cambio: number;
    metodoPago: MetodoPago;
    efectivoRecibido: number;
    pagos: Array<{ metodo: MetodoPago; monto: number }>;
    cliente: ClientePOS | null;
    numeroPedido: string;
  } | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pagoInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const resultsRef = useRef<HTMLDivElement>(null);
  const isSubmittingRef = useRef(false);

  // Calcular totales
  const subtotal = items.reduce((acc, item) => acc + item.subtotal, 0);
  const iva = 0;
  const total = subtotal + iva;
  const cambio = efectivoRecibido - total;

  // Función de búsqueda reutilizable
  const ejecutarBusqueda = async (query: string) => {
    if (!query.trim()) {
      setProductosFiltrados([]);
      setSelectedIndex(0);
      setSelectedPresIndex(0);
      setUltimaBusquedaEjecutada('');
      return;
    }
    setIsSearching(true);
    try {
      const response = await ProductsService.search(query.trim(), 20);
      if (response.success) {
        setProductosFiltrados(response.data);
        setSelectedIndex(0);
        setSelectedPresIndex(0);
        setUltimaBusquedaEjecutada(query);
      }
    } catch {
      // ignore
    } finally {
      setIsSearching(false);
    }
  };

  // Búsqueda debounced en backend
  useEffect(() => {
    if (!busqueda.trim()) {
      setProductosFiltrados([]);
      setSelectedIndex(0);
      setSelectedPresIndex(0);
      return;
    }
    
    const timer = setTimeout(() => {
      ejecutarBusqueda(busqueda);
    }, 200);
    
    return () => clearTimeout(timer);
  }, [busqueda]);

  // Limpiar búsqueda
  const limpiarBusqueda = () => {
    setBusqueda('');
    setProductosFiltrados([]);
    setSelectedIndex(0);
    setSelectedPresIndex(0);
    setUltimaBusquedaEjecutada('');
    searchInputRef.current?.focus();
  };

  // Agregar producto al carrito
  const agregarProductoConPresentacion = (producto: ProductSearchResult, presentacion: ProductSearchResult['presentaciones'][0]) => {
    const existente = items.find(item => item.producto.id === producto.id && item.presentacionId === presentacion.id);
    const cantidadActual = existente?.cantidad || 0;
    
    if (cantidadActual + 1 > presentacion.stock) {
      toast.error('Stock insuficiente');
      return;
    }
    
    if (existente) {
      setItems(items.map(item => 
        item.producto.id === producto.id && item.presentacionId === presentacion.id
          ? { ...item, cantidad: item.cantidad + 1, subtotal: (item.cantidad + 1) * presentacion.precio }
          : item
      ));
    } else {
      setItems([...items, { 
        producto, 
        presentacionId: presentacion.id,
        presentacionNombre: presentacion.nombre,
        factor: presentacion.factor,
        cantidad: 1, 
        subtotal: presentacion.precio 
      }]);
    }
    
    toast.success(`${producto.nombre} (${presentacion.nombre}) agregado`);
  };

  const agregarProducto = (producto: ProductSearchResult) => {
    const activas = producto.presentaciones?.filter(p => p.stock > 0) || [];
    if (activas.length === 0) {
      toast.error('Producto agotado');
      return;
    }
    if (activas.length === 1) {
      agregarProductoConPresentacion(producto, activas[0]);
      return;
    }
    setProductoParaSeleccionar(producto);
    setMostrarSelectorPresentacion(true);
  };

  // Actualizar cantidad (incremento/decremento)
  const actualizarCantidad = (productoId: string, presentacionId: string, delta: number) => {
    setItems(items.map(item => {
      if (item.producto.id === productoId && item.presentacionId === presentacionId) {
        const nuevaCantidad = Math.max(1, item.cantidad + delta);
        const presentacion = item.producto.presentaciones.find(p => p.id === presentacionId);
        if (!presentacion) return item;
        if (nuevaCantidad > presentacion.stock) {
          toast.error('Stock insuficiente');
          return item;
        }
        return { 
          ...item, 
          cantidad: nuevaCantidad, 
          subtotal: nuevaCantidad * presentacion.precio
        };
      }
      return item;
    }));
  };

  // Establecer cantidad directamente
  const establecerCantidad = (productoId: string, presentacionId: string, cantidad: number) => {
    setItems(items.map(item => {
      if (item.producto.id === productoId && item.presentacionId === presentacionId) {
        const presentacion = item.producto.presentaciones.find(p => p.id === presentacionId);
        if (!presentacion) return item;
        const nuevaCantidad = Math.max(1, Math.min(cantidad, presentacion.stock));
        if (cantidad > presentacion.stock) {
          toast.error('Stock insuficiente');
        }
        return { 
          ...item, 
          cantidad: nuevaCantidad, 
          subtotal: nuevaCantidad * presentacion.precio
        };
      }
      return item;
    }));
  };

  // Eliminar item
  const eliminarItem = (productoId: string, presentacionId: string) => {
    setItems(items.filter(item => !(item.producto.id === productoId && item.presentacionId === presentacionId)));
  };

  // Procesar venta
  const procesarVenta = async () => {
    if (items.length === 0) return;
    if (isSubmittingRef.current) {
      toast.error('La venta ya se está procesando. Por favor espere.');
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);
    try {
      const pedidoData = {
        clienteNombre: cliente?.nombre || 'Cliente de mostrador',
        clienteEmail: cliente?.email || '',
        clienteTelefono: cliente?.telefono || '',
        tipoDocumento: 'CC' as const,
        numeroDocumento: cliente?.documento || '',
        direccion: 'Venta en local físico',
        ciudad: 'Local',
        barrio: 'Local',
        metodoPago: pagos.length === 1 ? pagos[0].metodo : 'EFECTIVO',
        pagos: pagos.map(p => ({ metodo: p.metodo, monto: p.monto })),
        aplicarIva: false,
        usuarioId: user?.id,
        items: items.map(item => {
          const pres = item.producto.presentaciones.find(p => p.id === item.presentacionId);
          return {
            productoId: item.producto.id,
            nombreProducto: item.producto.nombre,
            presentacionId: item.presentacionId,
            nombrePresentacion: item.presentacionNombre,
            precioUnitario: pres?.precio || 0,
            factor: item.factor,
            cantidad: item.cantidad,
          };
        }),
      };

      const response = await OrdersService.create(pedidoData);
      
      if (response.success) {
        const ventaSubtotal = items.reduce((acc, item) => acc + item.subtotal, 0);
        const ventaIva = 0;
        const ventaTotal = ventaSubtotal + ventaIva;
        const totalPagado = pagos.reduce((s, p) => s + p.monto, 0);
        const ventaCambio = totalPagado - ventaTotal;
        const metodoPrincipal = pagos.length === 1 ? pagos[0].metodo : 'EFECTIVO';

        setTicketData({
          items: [...items],
          subtotal: ventaSubtotal,
          iva: ventaIva,
          total: ventaTotal,
          cambio: ventaCambio,
          metodoPago: metodoPrincipal,
          efectivoRecibido,
          pagos: [...pagos],
          cliente,
          numeroPedido: response.data.numeroPedido,
        });

        setNumeroPedido(response.data.numeroPedido);
        setPedidoId(response.data.id);
        setVentaCompletada(true);
        setItems([]);
        setCliente(null);
        setEfectivoRecibido(0);
        setPagos([]);
        toast.success('Venta completada exitosamente');
      } else {
        toast.error(response.message || 'Error al procesar la venta');
      }
    } catch (error: any) {
      toast.error(error.message || 'Error al procesar la venta');
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    const el = itemRefs.current[selectedIndex];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedIndex]);

  // Teclas rápidas
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ── Modal de Pago abierto ──
      if (mostrarModalPago && !mostrarModalCliente && !ventaCompletada) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          setMetodoPagoIndex(prev => {
            const next = Math.min(prev + 1, METODOS_PAGO.length - 1);
            setMetodoPago(METODOS_PAGO[next].id);
            return next;
          });
          return;
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          setMetodoPagoIndex(prev => {
            const next = Math.max(prev - 1, 0);
            setMetodoPago(METODOS_PAGO[next].id);
            return next;
          });
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          const totalPagado = pagos.reduce((s, p) => s + p.monto, 0);
          if (pagos.length > 0 && totalPagado >= total) {
            procesarVenta();
          }
          return;
        }
      }

      // ── Modal de Venta Completada abierto ──
      if (ventaCompletada && !mostrarModalAnulacion) {
        if (e.key === 'p' || e.key === 'P') {
          e.preventDefault();
          const ok = printThermalTicket('ticket-thermal', ticketData?.numeroPedido || numeroPedido);
          if (!ok) toast.error('Permita las ventanas emergentes para imprimir');
          return;
        }
        if (e.key === 'n' || e.key === 'N') {
          e.preventDefault();
          setVentaCompletada(false);
          setMostrarModalPago(false);
          setMostrarModalCliente(false);
          setMostrarModalAnulacion(false);
          setMostrarSelectorPresentacion(false);
          setEfectivoRecibido(0);
          setPedidoId('');
          setMotivoAnulacion('');
          setTicketData(null);
          setTimeout(() => searchInputRef.current?.focus(), 100);
          return;
        }
        if (e.key === 'Escape' && e.altKey) {
          e.preventDefault();
          setMostrarModalAnulacion(true);
          return;
        }
        return; // Bloquear otras teclas mientras está el modal de venta completada
      }

      // ── Navegación en lista de productos ──
      if (!mostrarModalPago && !mostrarModalCliente && !ventaCompletada && !mostrarSelectorPresentacion && productosFiltrados.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => {
            const next = Math.min(prev + 1, productosFiltrados.length - 1);
            setSelectedPresIndex(0);
            return next;
          });
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => {
            const next = Math.max(prev - 1, 0);
            setSelectedPresIndex(0);
            return next;
          });
          return;
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          const producto = productosFiltrados[selectedIndex];
          if (producto) {
            const activas = producto.presentaciones?.filter(p => (producto.stockBase / p.factor) >= 1) || [];
            setSelectedPresIndex(prev => Math.min(prev + 1, Math.max(activas.length - 1, 0)));
          }
          return;
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setSelectedPresIndex(prev => Math.max(prev - 1, 0));
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          const producto = productosFiltrados[selectedIndex];
          if (producto) {
            const activas = producto.presentaciones?.filter(p => (producto.stockBase / p.factor) >= 1) || [];
            const pres = activas[selectedPresIndex];
            if (pres) {
              agregarProductoConPresentacion(producto, pres);
              setBusqueda('');
              setProductosFiltrados([]);
              setSelectedIndex(0);
              setSelectedPresIndex(0);
              searchInputRef.current?.focus();
            }
          }
          return;
        }
      }

      if (e.key === 'F1') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'F2') {
        e.preventDefault();
        limpiarBusqueda();
      }
      if (e.key === 'F3') {
        e.preventDefault();
        setMostrarModalCliente(true);
      }
      if (e.key === 'F4') {
        e.preventDefault();
        if (items.length > 0) setMostrarModalPago(true);
      }
      if (e.key === 'Escape') {
        if (!mostrarModalPago && !mostrarModalCliente && !ventaCompletada && !mostrarSelectorPresentacion && busqueda) {
          limpiarBusqueda();
        } else {
          setMostrarModalPago(false);
          setMostrarModalCliente(false);
          setVentaCompletada(false);
          setMostrarSelectorPresentacion(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [productosFiltrados, selectedIndex, selectedPresIndex, mostrarModalPago, mostrarModalCliente, ventaCompletada, mostrarSelectorPresentacion, items.length, metodoPagoIndex, efectivoRecibido, total, ticketData, numeroPedido, mostrarModalAnulacion]);

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gray-100">
      {/* Header compacto */}
      <header className="px-4 py-4 flex items-center justify-between border-b flex-shrink-0 bg-white border-gray-200">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Punto de Venta
            </h1>
            {user && (
              <p className="text-xs text-gray-500">
                Vendedor: <span className="font-medium">{user.nombreCompleto}</span>
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-1 text-xs text-gray-500">
            <span className="px-1.5 py-0.5 bg-gray-700 rounded text-white text-[10px]">F1</span>
            <span>Buscar</span>
            <span className="px-1.5 py-0.5 bg-gray-700 rounded text-white text-[10px] ml-2">F3</span>
            <span>Cliente</span>
            <span className="px-1.5 py-0.5 bg-gray-700 rounded text-white text-[10px] ml-2">F4</span>
            <span>Pagar</span>
          </div>

          <button
            onClick={() => setMostrarModalCliente(true)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors min-w-[160px] max-w-[280px] ${
              cliente 
                ? 'bg-emerald-100 border-emerald-300 text-emerald-700' 
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <User className="w-4 h-4 shrink-0" />
            <span className="truncate">{cliente ? cliente.nombre : 'Cliente'}</span>
          </button>
        </div>
      </header>

      {/* Contenido principal */}
      <div className="flex-1 flex overflow-hidden pt-2">
        {/* Panel izquierdo */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Barra de búsqueda */}
          <div className="p-1.5 flex-shrink-0 bg-white/50">
            <div className="relative text-gray-900">
              <ScanBarcode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const texto = e.currentTarget.value;
                    // Si hay texto nuevo (diferente a la última búsqueda ejecutada), buscar inmediatamente (pistola láser)
                    if (texto.trim() && texto !== ultimaBusquedaEjecutada) {
                      setIsSearching(true);
                      try {
                        const response = await ProductsService.search(texto.trim(), 20);
                        if (response.success && response.data.length > 0) {
                          setProductosFiltrados(response.data);
                          setSelectedIndex(0);
                          setSelectedPresIndex(0);
                          setUltimaBusquedaEjecutada(texto);
                          if (response.data.length > 1) {
                            toast.success(`${response.data.length} variantes encontradas. Seleccione una.`);
                          }
                        } else {
                          toast.error('Producto no encontrado');
                        }
                      } catch {
                        toast.error('Error al buscar producto');
                      } finally {
                        setIsSearching(false);
                      }
                      return;
                    }
                    // Comportamiento normal: agregar producto seleccionado de resultados actuales
                    const producto = productosFiltrados[selectedIndex];
                    if (producto) {
                      const activas = producto.presentaciones?.filter(p => (producto.stockBase / p.factor) >= 1) || [];
                      const pres = activas[selectedPresIndex];
                      if (pres) {
                        agregarProductoConPresentacion(producto, pres);
                        limpiarBusqueda();
                      }
                    }
                  }
                }}
                placeholder="Buscar producto... (F1)"
                className="w-full pl-9 pr-9 py-3 text-sm rounded-lg border outline-none transition-all bg-white border-gray-200 focus:border-emerald-500 focus:ring-0"
                autoFocus
              />
              {busqueda && (
                <button
                  onClick={() => setBusqueda('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Contador de resultados */}
            <div className="mt-0.5 text-[10px] flex items-center gap-1.5 text-gray-500">
              {isSearching && (
                <Loader2 className="w-3 h-3 animate-spin text-emerald-600" />
              )}
              {busqueda ? (
                <span>{productosFiltrados.length} resultados</span>
              ) : (
                <span>Escribe para buscar</span>
              )}
            </div>
          </div>

          {/* Lista de productos */}
          <div className="flex-1 overflow-y-auto p-2 relative" ref={resultsRef}>
            {!busqueda ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <ScanBarcode className="w-16 h-16 mb-4 opacity-30" />
                <p className="text-sm font-medium">Escribe para buscar productos</p>
                <p className="text-xs mt-1">Usa ↑↓ para navegar, ←→ para presentación, Enter para agregar</p>
              </div>
            ) : productosFiltrados.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No se encontraron productos</p>
              </div>
            ) : (
              <div className="space-y-1">
                {productosFiltrados.map((prod, idx) => {
                  const isSelected = idx === selectedIndex;
                  const activas = prod.presentaciones?.filter(p => (prod.stockBase / p.factor) >= 1) || [];
                  const enCarrito = items.find(i => i.producto.id === prod.id);
                  
                  return (
                    <div
                      key={prod.id}
                      ref={el => { itemRefs.current[idx] = el; }}
                      className={`flex items-center gap-5 p-4 rounded-xl border transition-all ${
                        isSelected
                          ? 'ring-2 ring-emerald-500 bg-emerald-50'
                          : 'bg-white border-gray-200'
                      } ${enCarrito ? 'border-l-4 border-l-emerald-500' : ''}`}
                    >
                      {/* Foto */}
                      <div className="w-32 h-32 rounded-2xl overflow-hidden flex-shrink-0 bg-gray-100 shadow-md">
                        {getFirstProductImage(prod.imagenes) ? (
                          <img
                            src={getFirstProductImage(prod.imagenes)}
                            alt={prod.nombre}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-16 h-16 text-gray-400" />
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-lg truncate text-gray-900">
                          {prod.nombre}
                          {prod.variante && <span className="ml-2 text-emerald-600">({prod.variante})</span>}
                        </p>
                        {prod.ubicacion && (
                          <p className="text-base flex items-center gap-1 text-blue-600">
                            <MapPin className="w-5 h-5" />
                            {prod.ubicacion}
                          </p>
                        )}
                        {prod.requiereReceta && (
                          <span className="text-sm text-amber-600 flex items-center gap-0.5">
                            <Stethoscope className="w-4 h-4" /> Requiere receta
                          </span>
                        )}
                      </div>
                      
                      {/* Presentaciones */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {activas.map((pres, pIdx) => {
                          const isPresSelected = isSelected && pIdx === selectedPresIndex;
                          return (
                            <button
                              key={pres.id}
                              onClick={() => {
                                setSelectedIndex(idx);
                                setSelectedPresIndex(pIdx);
                                agregarProductoConPresentacion(prod, pres);
                                setBusqueda('');
                                setProductosFiltrados([]);
                                setSelectedIndex(0);
                                setSelectedPresIndex(0);
                                searchInputRef.current?.focus();
                              }}
                              className={`px-5 py-3 rounded-xl text-left transition-all border ${
                                isPresSelected
                                  ? 'bg-emerald-600 text-white border-emerald-600'
                                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                              }`}
                            >
                              <span className="text-base font-bold block">{pres.nombre}</span>
                              <span className="text-sm block opacity-90">
                                ${pres.precio.toLocaleString()} · {Math.floor(prod.stockBase / pres.factor)} disp.
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Panel derecho - Factura */}
        <div className="w-[26rem] flex flex-col border-l bg-white border-gray-200">
          {/* Header ticket */}
          <div className="px-4 py-3 border-b flex items-center justify-between border-gray-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <h2 className="font-bold text-sm text-gray-900">
                  Factura de venta
                </h2>
                <span className="text-xs text-gray-500">
                  {items.length} {items.length === 1 ? 'producto' : 'productos'}
                </span>
              </div>
            </div>
            {items.length > 0 && (
              <button
                onClick={() => setItems([])}
                className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Limpiar
              </button>
            )}
          </div>

          {/* Items del ticket */}
          <div className="flex-1 overflow-y-auto p-3">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-4 bg-emerald-50">
                  <ShoppingBag className="w-10 h-10 text-emerald-300" />
                </div>
                <p className="text-base font-semibold text-gray-700">El ticket está vacío</p>
                <p className="text-sm mt-1 text-center max-w-[200px]">
                  Haz clic en cualquier producto para agregarlo a la venta
                </p>
                <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
                  <span className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600">F1</span>
                  <span>Buscar producto</span>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((item, index) => (
                  <div 
                    key={item.producto.id + '-' + item.presentacionId}
                    className="p-3 rounded-lg border bg-white border-gray-200 shadow-sm"
                  >
                    {/* Fila superior: Número, nombre y eliminar */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex gap-2">
                        <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                          #{index + 1}
                        </span>
                        <div>
                          <p className="font-medium text-sm leading-tight text-gray-900">
                            {item.producto.nombre}
                          </p>
                          <p className="text-xs text-gray-500">
                            {item.presentacionNombre}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => eliminarItem(item.producto.id, item.presentacionId)}
                        className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded flex-shrink-0 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Fila inferior: Controles de cantidad y precio */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => actualizarCantidad(item.producto.id, item.presentacionId, -1)}
                          className="p-1.5 rounded-md transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <input
                          type="number"
                          min={1}
                          value={item.cantidad}
                          onChange={(e) => establecerCantidad(item.producto.id, item.presentacionId, Number(e.target.value))}
                          className="font-bold w-10 text-center text-base bg-transparent border-b-2 border-transparent focus:border-emerald-500 outline-none text-gray-900"
                        />
                        <button
                          onClick={() => actualizarCantidad(item.producto.id, item.presentacionId, 1)}
                          className="p-1.5 rounded-md transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600 text-base">
                          ${item.subtotal.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          ${(item.producto.presentaciones.find(p => p.id === item.presentacionId)?.precio || 0).toLocaleString()} c/u
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Totales */}
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span>
                <span className="font-medium">${subtotal.toLocaleString()}</span>
              </div>
              {/* IVA deshabilitado — siempre 0 */}
              <div className="flex justify-between text-xl font-bold pt-2 border-t-2 border-gray-300 text-gray-900">
                <span>TOTAL</span>
                <span className="text-emerald-600">${total.toLocaleString()}</span>
              </div>
            </div>

            {/* Botón pagar */}
            <button
              onClick={() => items.length > 0 && setMostrarModalPago(true)}
              disabled={items.length === 0 || loading}
              className="w-full mt-4 py-4 bg-emerald-600 text-white rounded-xl font-bold text-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  <CreditCard className="w-6 h-6" />
                  COBRAR
                  <span className="text-sm font-normal">(F4)</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Pago */}
      {mostrarModalPago && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-3xl shadow-2xl bg-white max-h-[95vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div>
                <h2 className="text-base font-bold text-gray-900">Cobrar</h2>
                <p className="text-xs text-gray-400">Ingresa los montos por cada método</p>
              </div>
              <button onClick={() => setMostrarModalPago(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto px-5 pb-5">
              {/* Total a pagar */}
              <div className="text-center py-4 rounded-2xl mb-4 bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-200">
                <p className="text-[10px] font-semibold text-emerald-100 uppercase tracking-widest mb-1">Total a pagar</p>
                <p className="text-4xl font-black text-white tracking-tight">${total.toLocaleString()}</p>
              </div>

              {(() => {
                const totalPagado = pagos.reduce((s, p) => s + p.monto, 0);
                const restante = Math.max(0, total - totalPagado);
                return (
                  <>
                    {/* Métodos de pago */}
                    <div className="mb-4">
                      <div className="space-y-2">
                        {METODOS_PAGO.map((metodo, idx) => {
                          const Icon = metodo.icon;
                          const pagoExistente = pagos.find(p => p.metodo === metodo.id);
                          const valorActual = pagoExistente ? pagoExistente.monto.toString() : '';
                          const estaActivo = pagoExistente && pagoExistente.monto > 0;
                          return (
                            <div
                              key={metodo.id}
                              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
                                estaActivo
                                  ? 'bg-emerald-50 shadow-sm ring-1 ring-emerald-200'
                                  : 'bg-gray-50/80 hover:bg-gray-50'
                              }`}
                            >
                              <div className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 transition-colors ${
                                estaActivo
                                  ? 'bg-emerald-500 text-white shadow-sm shadow-emerald-200'
                                  : 'bg-white text-gray-400 shadow-sm'
                              }`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <span className={`font-semibold text-sm w-20 ${estaActivo ? 'text-emerald-800' : 'text-gray-600'}`}>
                                {metodo.label}
                              </span>
                              <div className="flex-1 relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-300 font-bold">$</span>
                                <input
                                  ref={(el) => { pagoInputRefs.current[idx] = el; }}
                                  type="text"
                                  inputMode="numeric"
                                  value={valorActual ? Number(valorActual).toLocaleString('en-US') : ''}
                                  onChange={(e) => {
                                    const raw = e.target.value.replace(/[^0-9]/g, '');
                                    const val = raw === '' ? 0 : Number(raw);
                                    if (val > 0) {
                                      setPagos(prev => {
                                        const pidx = prev.findIndex(p => p.metodo === metodo.id);
                                        if (pidx >= 0) {
                                          const copy = [...prev];
                                          copy[pidx] = { metodo: metodo.id, monto: val };
                                          return copy;
                                        }
                                        return [...prev, { metodo: metodo.id, monto: val }];
                                      });
                                      if (metodo.id === 'EFECTIVO') setEfectivoRecibido(val);
                                    } else {
                                      setPagos(prev => prev.filter(p => p.metodo !== metodo.id));
                                      if (metodo.id === 'EFECTIVO') setEfectivoRecibido(0);
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'ArrowDown') {
                                      e.preventDefault();
                                      const next = pagoInputRefs.current[idx + 1];
                                      if (next) next.focus();
                                    } else if (e.key === 'ArrowUp') {
                                      e.preventDefault();
                                      const prev = pagoInputRefs.current[idx - 1];
                                      if (prev) prev.focus();
                                    } else if (e.key === 'Enter') {
                                      e.preventDefault();
                                      const totalPagado = pagos.reduce((s, p) => s + p.monto, 0);
                                      if (pagos.length > 0 && totalPagado >= total) {
                                        procesarVenta();
                                      }
                                    }
                                  }}
                                  className={`w-full pl-8 pr-3 py-2 text-base font-bold rounded-lg border-0 outline-none transition-all text-right ${
                                    estaActivo
                                      ? 'bg-white text-emerald-700 shadow-sm ring-2 ring-emerald-300 focus:ring-emerald-500'
                                      : 'bg-white/60 text-gray-700 ring-1 ring-gray-200 focus:ring-emerald-400 focus:bg-white'
                                  }`}
                                  placeholder="0"
                                  autoFocus={metodo.id === 'EFECTIVO'}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Resumen */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-gray-50 rounded-xl p-3 text-center">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Total</p>
                        <p className="text-base font-black text-gray-800">${total.toLocaleString()}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 text-center">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Pagado</p>
                        <p className="text-base font-black text-gray-800">${totalPagado.toLocaleString()}</p>
                      </div>
                      <div className={`rounded-xl p-3 text-center ${
                        totalPagado >= total
                          ? 'bg-emerald-100'
                          : 'bg-amber-100'
                      }`}>
                        <p className={`text-[10px] font-bold uppercase tracking-wider mb-0.5 ${
                          totalPagado >= total ? 'text-emerald-600' : 'text-amber-600'
                        }`}>
                          {totalPagado >= total ? 'Cambio' : 'Falta'}
                        </p>
                        <p className={`text-xl font-black ${
                          totalPagado >= total ? 'text-emerald-700' : 'text-amber-700'
                        }`}>
                          ${Math.abs(totalPagado - total).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {/* Alerta de cambio grande */}
                    {(() => {
                      const pagoEfectivo = pagos.find(p => p.metodo === 'EFECTIVO');
                      const cambio = totalPagado - total;
                      if (!pagoEfectivo || cambio <= 0) return null;
                      return (
                        <div className="py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-center mb-4 shadow-lg shadow-emerald-200">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-100 mb-0.5">Devolver en efectivo</p>
                          <p className="text-3xl font-black">${cambio.toLocaleString()}</p>
                        </div>
                      );
                    })()}

                    {/* Botones de acción */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setMostrarModalPago(false); setPagos([]); setEfectivoRecibido(0); }}
                        className="flex-1 py-2.5 rounded-xl border font-semibold text-sm transition-all border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={procesarVenta}
                        disabled={loading || pagos.length === 0 || totalPagado < total}
                        className="flex-[2] py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-bold text-sm hover:from-emerald-600 hover:to-teal-700 disabled:opacity-40 disabled:from-gray-300 disabled:to-gray-400 transition-all shadow-lg shadow-emerald-200"
                      >
                        {loading ? (
                          <span className="flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Procesando...
                          </span>
                        ) : (
                          totalPagado >= total ? '✓ Completar Venta' : `Faltan $${restante.toLocaleString()}`
                        )}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Búsqueda de Cliente */}
      <ClienteSearchModal
        isOpen={mostrarModalCliente}
        onClose={() => setMostrarModalCliente(false)}
        onSelect={(c) => setCliente(c)}
        onClear={() => setCliente(null)}
        currentCliente={cliente}
      />

      {/* Modal de Selección de Presentación */}
      {mostrarSelectorPresentacion && productoParaSeleccionar && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          tabIndex={-1}
          ref={(el) => { if (el) el.focus(); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const activas = productoParaSeleccionar.presentaciones?.filter(p => (productoParaSeleccionar.stockBase / p.factor) >= 1) || [];
              if (activas[0]) {
                agregarProductoConPresentacion(productoParaSeleccionar, activas[0]);
                setMostrarSelectorPresentacion(false);
                setProductoParaSeleccionar(null);
              }
            }
            if (e.key === 'Escape') {
              setMostrarSelectorPresentacion(false);
              setProductoParaSeleccionar(null);
            }
          }}
        >
          <div className="w-full max-w-sm rounded-xl shadow-2xl p-5 bg-white">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">
                Seleccionar presentación
              </h2>
              <button onClick={() => setMostrarSelectorPresentacion(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                {getFirstProductImage(productoParaSeleccionar.imagenes) ? (
                  <img
                    src={getFirstProductImage(productoParaSeleccionar.imagenes)}
                    alt={productoParaSeleccionar.nombre}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-6 h-6 text-gray-400" />
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {productoParaSeleccionar.nombre}
                </p>
                {productoParaSeleccionar.ubicacion && (
                  <p className="text-[11px] flex items-center gap-1 text-blue-600">
                    <MapPin className="w-3 h-3" />
                    {productoParaSeleccionar.ubicacion}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              {productoParaSeleccionar.presentaciones?.filter(p => (productoParaSeleccionar.stockBase / p.factor) >= 1).map((pres) => (
                <button
                  key={pres.id}
                  onClick={() => {
                    agregarProductoConPresentacion(productoParaSeleccionar, pres);
                    setMostrarSelectorPresentacion(false);
                    setProductoParaSeleccionar(null);
                  }}
                  className="w-full p-3 rounded-lg border text-left transition-all bg-gray-50 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm text-gray-900">
                        {pres.nombre}
                      </p>
                      <p className="text-xs text-gray-500">
                        Factor: {pres.factor} · Stock: {Math.floor(productoParaSeleccionar.stockBase / pres.factor)}
                      </p>
                    </div>
                    <p className="text-emerald-600 font-bold text-sm">
                      ${pres.precio.toLocaleString()}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Venta Completada */}
      {ventaCompletada && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl shadow-2xl p-6 text-center bg-white">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold mb-1 text-gray-900">
              ¡Venta Exitosa!
            </h2>
            <p className="mb-2 text-gray-600">
              Factura: <span className="font-mono font-bold text-emerald-600">{ticketData?.numeroPedido || numeroPedido}</span>
            </p>
            <p className="text-2xl font-bold text-emerald-600 mb-4">
              ${(ticketData?.total ?? total).toLocaleString()}
            </p>
            
            <div className="p-3 rounded-lg mb-4 bg-gray-100">
              <div className="flex justify-between text-xs mb-1">
                <span className={'text-gray-600'}>Método:</span>
                <span className="font-medium">{ticketData?.metodoPago || metodoPago}</span>
              </div>
              {ticketData?.metodoPago === 'EFECTIVO' && (ticketData?.cambio ?? cambio) > 0 && (
                <div className="flex justify-between text-xs">
                  <span className={'text-gray-600'}>Cambio:</span>
                  <span className="font-medium text-emerald-600">${(ticketData?.cambio ?? cambio).toLocaleString()}</span>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setVentaCompletada(false);
                  setMostrarModalPago(false);
                  setMostrarModalCliente(false);
                  setMostrarModalAnulacion(false);
                  setMostrarSelectorPresentacion(false);
                  setEfectivoRecibido(0);
                  setPedidoId('');
                  setMotivoAnulacion('');
                  setTicketData(null);
                  // Enfocar el campo de búsqueda para la nueva venta
                  setTimeout(() => searchInputRef.current?.focus(), 100);
                }}
                className="flex-1 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 text-sm"
              >
                Nueva Venta
              </button>
              <button
                className="flex-1 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-lg font-bold hover:bg-red-100 flex items-center justify-center gap-1.5 text-sm"
                onClick={() => setMostrarModalAnulacion(true)}
              >
                <X className="w-4 h-4" />
                Anular
              </button>
              <button
                className="flex-1 py-2.5 bg-emerald-600 text-white rounded-lg font-bold hover:bg-emerald-700 flex items-center justify-center gap-1.5 text-sm"
                onClick={() => {
                  const ok = printThermalTicket('ticket-thermal', ticketData?.numeroPedido || numeroPedido);
                  if (!ok) toast.error('Permita las ventanas emergentes para imprimir');
                }}
              >
                <Printer className="w-4 h-4" />
                Imprimir
              </button>
            </div>
            <div className="mt-3 text-center text-[10px] text-gray-400">
              <span className="px-1 py-0.5 bg-gray-200 rounded text-gray-600">P</span> Imprimir · <span className="px-1 py-0.5 bg-gray-200 rounded text-gray-600">N</span> Nueva · <span className="px-1 py-0.5 bg-gray-200 rounded text-gray-600">Alt+Esc</span> Anular
            </div>
          </div>
        </div>
      )}

      {/* Ticket térmico para impresión */}
      {ticketData && (
        <TicketThermal
          numeroPedido={ticketData.numeroPedido}
          fecha={new Date().toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}
          vendedor={user?.nombreCompleto || user?.email}
          cliente={ticketData.cliente?.nombre}
          items={ticketData.items.map(item => ({
            nombre: item.producto.nombre,
            presentacion: item.presentacionNombre,
            cantidad: item.cantidad,
            precioUnitario: item.subtotal / item.cantidad,
            subtotal: item.subtotal,
          }))}
          subtotal={ticketData.subtotal}
          iva={ticketData.iva}
          total={ticketData.total}
          metodoPago={ticketData.metodoPago === 'EFECTIVO' ? 'Efectivo' : ticketData.metodoPago === 'TARJETA' ? 'Tarjeta' : ticketData.metodoPago === 'NEQUI' ? 'Nequi' : ticketData.metodoPago === 'DAVIPLATA' ? 'Daviplata' : 'Transferencia'}
          efectivoRecibido={ticketData.metodoPago === 'EFECTIVO' ? ticketData.efectivoRecibido : undefined}
          cambio={ticketData.metodoPago === 'EFECTIVO' && ticketData.cambio > 0 ? ticketData.cambio : undefined}
        />
      )}

      {/* Modal de Anulación */}
      {mostrarModalAnulacion && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl shadow-2xl p-6 bg-white">
            <h3 className="text-lg font-bold mb-2 text-gray-900">
              Anular Venta
            </h3>
            <p className="text-sm mb-4 text-gray-600">
              Pedido: <span className="font-mono font-bold">{numeroPedido}</span>
            </p>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Motivo de la anulación
            </label>
            <textarea
              value={motivoAnulacion}
              onChange={(e) => setMotivoAnulacion(e.target.value)}
              placeholder="Ej: Cliente se equivocó de producto"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white border-gray-300 text-gray-900"
              rows={3}
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  setMostrarModalAnulacion(false);
                  setMotivoAnulacion('');
                }}
                className="flex-1 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 text-sm"
              >
                Cancelar
              </button>
              <button
                disabled={!motivoAnulacion.trim() || loadingAnulacion}
                onClick={async () => {
                  if (!pedidoId || !motivoAnulacion.trim()) return;
                  setLoadingAnulacion(true);
                  try {
                    await OrdersService.updateStatus(pedidoId, 'CANCELLED', motivoAnulacion.trim());
                    toast.success('Venta anulada correctamente. Stock restaurado.');
                    setMostrarModalAnulacion(false);
                    setMotivoAnulacion('');
                    setVentaCompletada(false);
                    setEfectivoRecibido(0);
                    setPedidoId('');
                  } catch (err: any) {
                    toast.error(err.message || 'Error al anular la venta');
                  } finally {
                    setLoadingAnulacion(false);
                  }
                }}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:opacity-50 text-sm"
              >
                {loadingAnulacion ? 'Anulando...' : 'Confirmar Anulación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POSPage;
